/**
 * Cluster instances (master/worker VMs): list/detail/action + disk/snapshot/
 * NIC types, plus on-demand kubeconfig fetch.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { serviceApi, unwrap, k8sPath, arr, k8sKeys } from './_internal';
import { serviceApiV3 } from '@/api/client';

export interface ClusterInstance {
  id: string;
  name?: string;
  status?: string;
  /** `role` is encoded in the top-level `type` (e.g. `kubernetes=worker`); a
   *  flat `role` is not sent. `roleOf()` derives the displayed value. */
  role?: string;
  /** Top-level node type, e.g. `kubernetes=worker` / `kubernetes=master`. */
  type?: string;
  /** Flat IP is not sent on the list payload; the real value is `metadata.ip`. */
  ip?: string;
  /** Present on the list payload (same shape the @console instance list reads). */
  zone?: string;
  metadata?: {
    /** Assigned IP lives here on the list payload. */
    ip?: string;
    flavor?: string;
    type?: string;
    image?: { name?: string; distro?: string };
    nics?: { nic_id?: number | string; ip?: string; mac?: string; network?: string; network_id?: string }[];
    resource?: { service?: { service_uuid?: string } };
  };
}

/** Assigned IP from the payload: `metadata.ip` is canonical; flat `ip` /
 *  `metadata.nics[0].ip` kept as fallbacks for the detail shape. */
export function ipOf(inst: ClusterInstance): string | undefined {
  return inst.metadata?.ip || inst.ip || inst.metadata?.nics?.[0]?.ip;
}

/** Node role from the `kubernetes=worker` top-level `type`, with `role` fallback. */
export function roleOf(inst: ClusterInstance): string | undefined {
  return inst.role || inst.type?.split('=').pop() || undefined;
}

export function useClusterInstances(serviceId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: [...k8sKeys.cluster(serviceId ?? '', id ?? ''), 'instances'] as const,
    enabled: !!serviceId && !!id,
    retry: false,
    refetchInterval: 20_000,
    queryFn: async () => arr<ClusterInstance>(unwrap(await serviceApi.get(k8sPath(serviceId!, `clusters/${id}/instances`))), 'instances'),
  });
}

export function useClusterInstanceAction(serviceId: string, clusterId: string, instanceId: string, computeServiceId: string | undefined, options?: UseMutationOptions<unknown, unknown, string>) {
  const qc = useQueryClient();
  return useMutation({
    // Cluster VMs are OpenNebula compute instances owned by a separate compute
    // (s10003) service — the k8s s10015 route only exposes index/show. Power actions
    // go through that compute service's perform endpoint via the @mgmt v3 strangler
    // proxy (so the action is authorized once and written to the activity log),
    // with the owning service uuid read from the instance's
    // `metadata.resource.service.service_uuid` (the legacy console's own-service POST).
    mutationFn: async (perform: string) => {
      if (!computeServiceId) throw new Error('Cannot resolve the compute service for this instance');
      return (await serviceApiV3.post(`/services/${computeServiceId}/s10003/instances/${instanceId}/perform`, { perform })).data;
    },
    ...options,
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: [...k8sKeys.cluster(serviceId, clusterId), 'instances'] }); options?.onSuccess?.(...a); },
  });
}

/** A disk on a cluster instance (`metadata.disks[]`). */
export interface ClusterInstanceDisk {
  disk_id: string;
  image?: string;
  type?: string;
  size?: number | string;
}
/** A disk snapshot on a cluster instance (`metadata.snapshots[]`). */
export interface ClusterInstanceSnapshot {
  id: string;
  name?: string;
  date?: number | string;
  size?: number | string;
}
/** A NIC on a cluster instance (`metadata.nics[]`). */
export interface ClusterInstanceNic {
  nic_id?: number | string;
  ip?: string;
  mac?: string;
  network?: string;
  network_id?: string;
}

/** Single cluster-instance detail (`/clusters/:id/instances/:iid`). Adds the
 *  underlying VM's disks / snapshots / NICs / resources beyond the list row. */
export interface ClusterInstanceDetail extends ClusterInstance {
  zone?: string;
  metadata?: {
    ip?: string;
    flavor?: string;
    image?: { name?: string; distro?: string };
    resources?: { cores?: number; memory?: number; volumes?: number; networks?: number };
    floating_ip?: string;
    disks?: ClusterInstanceDisk[];
    snapshots?: ClusterInstanceSnapshot[];
    nics?: ClusterInstanceNic[];
  };
}

/** GET a single cluster instance (master/worker VM) with full detail.
 *  NOTE: disk-snapshot CRUD on these VMs reuses the s10003 compute hooks
 *  (`useCreateSnapshot`/`useRevertSnapshot`/`useDeleteSnapshot` from
 *  `@/api/opennebula`), exactly as @console does — there is no s10015 snapshot
 *  endpoint. Terminate/reboot/poweroff go through `useClusterInstanceAction`. */
export function useClusterInstance(serviceId: string | undefined, clusterId: string | undefined, instanceId: string | undefined) {
  return useQuery({
    queryKey: [...k8sKeys.cluster(serviceId ?? '', clusterId ?? ''), 'instances', instanceId ?? ''] as const,
    enabled: !!serviceId && !!clusterId && !!instanceId,
    retry: false,
    refetchInterval: 20_000,
    queryFn: async () => {
      const body = unwrap<{ instance?: ClusterInstanceDetail } | ClusterInstanceDetail>(
        await serviceApi.get(k8sPath(serviceId!, `clusters/${clusterId}/instances/${instanceId}`)),
      );
      return (body && typeof body === 'object' && 'instance' in body ? body.instance : body) as ClusterInstanceDetail;
    },
  });
}

/**
 * Fetch kubeconfig YAML on demand (not cached). `expiresDays` sets the client
 * certificate lifetime; omitted → the backend default (90 days). Backend accepts
 * 1–365; callers may enforce a tighter range.
 */
export async function fetchKubeconfig(serviceId: string, clusterId: string, expiresDays?: number): Promise<string> {
  const payload = expiresDays != null ? { expires_days: expiresDays } : {};
  const res = await serviceApi.post(k8sPath(serviceId, `clusters/${clusterId}/kubeconfig`), payload);
  // Real response key is `config` (legacy reads data.config); `kubeconfig` kept as fallback.
  const body = unwrap<{ config?: string; kubeconfig?: string } | string>(res);
  return typeof body === 'string' ? body : (body.config ?? body.kubeconfig ?? '');
}
